import { useEffect, useState, useCallback } from "react";
import { useProject } from "@/contexts/ProjectContext";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Pencil, ExternalLink, FileText, Sheet, Video, Palette, StickyNote, Link2, Search } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["Google Doc", "Google Sheet", "Excel Link", "Video Link", "Design Link", "Note", "Other"];

const TYPE_ICON = {
  "Google Doc": FileText,
  "Google Sheet": Sheet,
  "Excel Link": Sheet,
  "Video Link": Video,
  "Design Link": Palette,
  "Note": StickyNote,
  "Other": Link2,
};

const empty = () => ({ title: "", type: "Google Doc", url: "", description: "", tags: [] });

export default function Resources() {
  const { selected } = useProject();
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ q: "", type: "all" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [tagsInput, setTagsInput] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!selected) return;
    const params = {};
    if (filters.q) params.q = filters.q;
    if (filters.type && filters.type !== "all") params.type = filters.type;
    const { data } = await api.get(`/projects/${selected.id}/resources`, { params });
    setItems(data);
  }, [selected, filters]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(empty());
    setTagsInput("");
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ title: r.title, type: r.type, url: r.url || "", description: r.description || "", tags: r.tags || [] });
    setTagsInput((r.tags || []).join(", "));
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const tags = tagsInput.split(",").map((s) => s.trim()).filter(Boolean);
      const payload = { ...form, tags };
      if (editing) {
        await api.patch(`/projects/${selected.id}/resources/${editing.id}`, payload);
        toast.success("Resource updated");
      } else {
        await api.post(`/projects/${selected.id}/resources`, payload);
        toast.success("Resource added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this resource?")) return;
    await api.delete(`/projects/${selected.id}/resources/${id}`);
    toast.success("Deleted");
    load();
  };

  if (!selected) return <div className="text-sm text-slate-500">Select a project first.</div>;

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="resources-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Resources</h1>
          <p className="text-sm text-slate-500 mt-1">Docs, sheets, videos, notes — all in one place</p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-resource-button">
          <Plus className="h-4 w-4 mr-2" /> Add Resource
        </Button>
      </div>

      <Card className="rounded-2xl border-slate-200 shadow-sm">
        <CardContent className="p-5 flex flex-wrap gap-3 items-end">
          <div className="space-y-1 flex-1 min-w-[200px]">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input className="pl-9" placeholder="Search title, tag, description..." value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="resource-search" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Type</Label>
            <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
              <SelectTrigger className="w-48" data-testid="resource-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="cards">
        <TabsList>
          <TabsTrigger value="cards" data-testid="view-cards">Cards</TabsTrigger>
          <TabsTrigger value="table" data-testid="view-table">Table</TabsTrigger>
        </TabsList>
        <TabsContent value="cards">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.length === 0 ? (
              <div className="col-span-full text-center text-slate-500 py-12">No resources match.</div>
            ) : items.map((r) => {
              const Icon = TYPE_ICON[r.type] || Link2;
              return (
                <Card key={r.id} className="rounded-2xl border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`resource-card-${r.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="h-9 w-9 rounded-lg bg-indigo-50 text-indigo-700 grid place-items-center">
                        <Icon className="h-4 w-4" />
                      </div>
                      <Badge variant="secondary" className="text-[10px]">{r.type}</Badge>
                    </div>
                    <h3 className="font-display font-semibold text-slate-900 mb-1 line-clamp-2">{r.title}</h3>
                    {r.description && <p className="text-sm text-slate-500 line-clamp-2 mb-3">{r.description}</p>}
                    {r.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {r.tags.map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                      <span className="text-xs text-slate-400">{formatDate(r.created_at)}</span>
                      <div className="flex items-center gap-1">
                        {r.url && (
                          <a href={r.url} target="_blank" rel="noopener noreferrer" data-testid={`open-resource-${r.id}`}>
                            <Button size="sm" variant="ghost"><ExternalLink className="h-3.5 w-3.5" /></Button>
                          </a>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-resource-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`delete-resource-${r.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
        <TabsContent value="table">
          <Card className="rounded-2xl border-slate-200 shadow-sm overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No resources.</TableCell></TableRow>
                ) : items.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.title}</TableCell>
                    <TableCell><Badge variant="secondary" className="text-[10px]">{r.type}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-500">{(r.tags || []).join(", ")}</TableCell>
                    <TableCell>{formatDate(r.created_at)}</TableCell>
                    <TableCell className="text-right">
                      {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="ghost"><ExternalLink className="h-3.5 w-3.5" /></Button></a>}
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r.id)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit Resource" : "Add Resource"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="resource-form-title" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="resource-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tags (comma separated)</Label>
                <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="design, brand" data-testid="resource-form-tags" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>URL (optional)</Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." data-testid="resource-form-url" />
            </div>
            <div className="space-y-2">
              <Label>Description / Note</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="resource-form-desc" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="resource-form-save">{saving ? "Saving..." : editing ? "Save" : "Add"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
